import {
  CHAT_LIMITS,
  type GatheringState,
  type PollState,
} from '@twinden/shared';
import type { ChatConversation } from './ConversationDialogs';
import type { OpenedE2EE } from '../../lib/e2ee/session';
import { appLocale } from '../../lib/format';
import { t } from '../../i18n';

export interface MessageMetadata {
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  duration?: number;
  waveform?: number[];
  width?: number;
  height?: number;
  thumbnailUrl?: string;
}

// Message as returned by the API (differs from the shared schema).
export interface ChatMessage {
  id: string;
  conversationId: string;
  sender: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
    /** A deleted account (M51). */
    deleted?: boolean;
  } | null;
  content: string;
  type: string;
  metadata: Record<string, unknown> | null;
  readBy: string[];
  starredByMe?: boolean;
  // M20 (docs/chat-essentials.md §3)
  replyTo?: ReplySnapshot | null;
  reactions?: { userId: string; emoji: string }[];
  editedAt?: string | null;
  deletedAt?: string | null;
  // M22 (docs/chat-round-two.md §3)
  mentions?: string[];
  pinnedAt?: string | null;
  forwarded?: boolean;
  // M23 (docs/reliable-sending.md, D32)
  clientId?: string | null;
  // M25 (docs/search-and-mentions.md, D43)
  mentionsEveryone?: boolean;
  // M28 (docs/end-to-end-encryption.md): sealed on the wire, opened here.
  encryption?: string | null;
  keyEpoch?: number | null;
  e2ee?: OpenedE2EE;
  // M60 (docs/chat-round-three.md §4): only on polls.
  poll?: PollState;
  // M71 (docs/gatherings.md, D282): only on gatherings.
  gathering?: GatheringState;
  // M64 (docs/chat-round-six.md, D238): hidden from this time on.
  expiresAt?: string | null;
  createdAt: string;
}

/**
 * The name a sender is shown by. A sealed item that opened is named from its
 * bound sender id through the current members (D327), looked up now rather
 * than when it opened: one opened before the members loaded was left with
 * no name, and gets it when they arrive (#113). The name it opened with
 * (kept by this device, or none) stays when the members don't name them.
 * Anything else, a deleted account (M51) and an item that can't be verified
 * (never shown as from anyone) keep what they have.
 */
export function shownSenderName(
  item: {
    senderId: string | null | undefined;
    name: string;
    e2ee?: OpenedE2EE;
    deleted?: boolean;
  },
  nameOf: (userId: string) => string | null
): string {
  const opened = item.e2ee && !item.e2ee.locked && !item.e2ee.unverified;
  if (!opened || !item.senderId || item.deleted) return item.name;
  return nameOf(item.senderId) ?? item.name;
}

/** Every type this app can show (D222); anything else asks to update. */
export const KNOWN_MESSAGE_TYPES = new Set([
  'text',
  'image',
  'gif',
  'video',
  'voice',
  'file',
  'system',
  'album',
  'poll',
  'gathering',
]);

export function isKnownType(type: string): boolean {
  return KNOWN_MESSAGE_TYPES.has(type);
}

export interface ReplySnapshot {
  id: string;
  senderId: string | null;
  senderName: string;
  /** A deleted account (M51): named in the app's own words. */
  senderDeleted?: boolean;
  type: string;
  preview: string;
  deleted: boolean;
  createdAt: string;
  /** M64: the quote goes when the quoted message disappears (D238). */
  expiresAt?: string | null;
  /** Set by this device's opener only (D347): `bound: false` is older (D328). */
  e2ee?: OpenedE2EE;
}

/**
 * A gathering's line in quotes, previews and the outbox (M71, D282):
 * "📅 Dinner". The glyph reads the same in every language, so it isn't a
 * catalog string; the title is the sender's own words, opened on the device.
 */
export function gatheringQuote(title: string): string {
  return `📅 ${title}`;
}

// What a quoted message looks like in a reply bar or bubble.
export function replyLabel(message: Pick<ChatMessage, 'type' | 'content'>) {
  const labels: Record<string, string> = {
    image: t('chat.type.image'),
    gif: 'GIF',
    video: t('chat.type.video'),
    voice: t('chat.type.voice'),
    file: t('chat.type.file'),
  };
  const text = message.content.replace(/\s+/g, ' ').trim();
  // M60: an album quotes its caption, a poll its question (both opened on
  // the device); an unknown type never shows its raw content (D222).
  if (message.type === 'album') return text || t('chat.type.album');
  if (message.type === 'poll') {
    return text
      ? t('chat.poll.quote', { question: text })
      : t('chat.type.poll');
  }
  // M71 (D282): "📅 Dinner", from the title opened on the device.
  if (message.type === 'gathering') {
    return text ? gatheringQuote(text) : t('chat.type.gathering');
  }
  if (!isKnownType(message.type)) return t('chat.type.unknown');
  return labels[message.type] ?? text;
}

/** A new message for the chat list, from the socket. */
export interface LiveMessage {
  conversationId: string;
  createdAt: string;
  type: string;
  content: string;
  sender?: { id: string; displayName: string } | null;
  metadata?: Record<string, unknown> | null;
  // Set by this device's opener only (D347); `bound: false` is older (D328).
  e2ee?: OpenedE2EE;
}

/** An album's counts from its plain metadata (D219). */
export function albumCounts(
  metadata: Record<string, unknown> | null | undefined
): { photos: number; videos: number } | undefined {
  const album = metadata?.album as
    | { photos?: unknown; videos?: unknown }
    | undefined;
  if (!album) return undefined;
  return {
    photos: typeof album.photos === 'number' ? album.photos : 0,
    videos: typeof album.videos === 'number' ? album.videos : 0,
  };
}

/**
 * The chat list after a new message: its chat's preview line and time move
 * on, and it counts as unread only when someone else wrote it in a chat
 * that isn't open. `own` is a message:sent from one of your devices (D206):
 * never unread.
 */
export function withNewMessage(
  conversations: ChatConversation[],
  message: LiveMessage,
  {
    activeId,
    currentUserId,
    own,
  }: { activeId: string | null; currentUserId: string | null; own: boolean }
): ChatConversation[] {
  if (!conversations.some((c) => c.id === message.conversationId)) {
    return conversations;
  }
  // A group info line (D234) isn't a message anyone waits for: it shows on
  // the tile but neither counts as unread nor moves the chat up (D231).
  const infoLine = isGroupInfoLine(message);
  const counts =
    !own &&
    !infoLine &&
    message.conversationId !== activeId &&
    message.sender?.id !== currentUserId;
  return conversations.map((conversation) =>
    conversation.id === message.conversationId
      ? {
          ...conversation,
          lastMessageAt: infoLine
            ? conversation.lastMessageAt
            : message.createdAt,
          // Keep the home tile's preview line live (Living Room).
          lastMessage: {
            senderId: message.sender?.id ?? '',
            senderName: message.sender?.displayName ?? t('chat.page.someone'),
            type: message.type,
            preview:
              message.type === 'text' ||
              message.type === 'system' ||
              message.type === 'album' ||
              message.type === 'poll' ||
              message.type === 'gathering'
                ? message.content.replace(/\s+/g, ' ').trim()
                : replyLabel(message),
            album: albumCounts(message.metadata),
            // Unbound stays marked on the tile too (D328).
            ...(message.e2ee ? { e2ee: message.e2ee } : {}),
            ...(message.type === 'system'
              ? { metadata: message.metadata ?? null }
              : {}),
            createdAt: message.createdAt,
          },
          unreadCount: (conversation.unreadCount ?? 0) + (counts ? 1 : 0),
        }
      : conversation
  );
}

/** A system line about a group's name, picture or description (D234). */
export function isGroupInfoLine(
  message: Pick<LiveMessage, 'type' | 'metadata'>
): boolean {
  const event = message.metadata?.event;
  return (
    message.type === 'system' &&
    typeof event === 'string' &&
    event.startsWith('group_')
  );
}

export function conversationName(
  conversation: ChatConversation,
  currentUserId: string | undefined,
  /** What you call people (D143): member id → label. */
  labels: Record<string, string> = {}
): string {
  if (conversation.name) return conversation.name;
  const others = conversation.participants.filter(
    (participant) => participant.id !== currentUserId
  );
  return (
    others
      .map((participant) => labels[participant.id] ?? participant.displayName)
      .join(', ') ||
    // A DM with someone who deleted their account (M51).
    conversation.formerParticipants?.map((p) => p.displayName).join(', ') ||
    t('chat.unknown')
  );
}

// First http(s) URL in a message, trimmed of trailing punctuation. Mirrors the
// server-side extraction used when saving a link.
export function firstUrl(content: string): string | null {
  const match = content.match(/https?:\/\/[^\s<>"']+/i);
  if (!match) return null;
  return match[0].replace(/[.,;:!?)\]}]+$/, '');
}

export function attachmentMetadata(
  message: ChatMessage
): { fileName: string; mimeType: string } | null {
  if (message.type === 'text' || message.type === 'system') return null;
  if (!message.content.startsWith('/uploads/')) return null;
  const metadata = (message.metadata ?? {}) as {
    fileName?: string;
    mimeType?: string;
  };
  if (!metadata.fileName || !metadata.mimeType) return null;
  return { fileName: metadata.fileName, mimeType: metadata.mimeType };
}

export function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatBytes(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// "Today", "Yesterday", a weekday within the last week, else a full date.
export function formatDayLabel(date: Date, now = new Date()): string {
  if (isSameDay(date, now)) return t('chat.list.today');

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return t('chat.list.yesterday');

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const daysAgo = (startOfToday.getTime() - date.getTime()) / 86_400_000;
  if (daysAgo < 6) {
    return date.toLocaleDateString(appLocale(), { weekday: 'long' });
  }

  return date.toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'long',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

// How long to wait for the browser to give us a frame before sending the
// video without a preview rather than not at all.
const VIDEO_META_TIMEOUT_MS = 8000;

/**
 * A video's size, length and a 640px JPEG preview. iPhone Safari loads no
 * picture for `preload="metadata"` and may never finish a seek, which left
 * videos without previews (2026-10-22): this loads data, waits for a decoded
 * frame after seeking, and gives up on the preview, never on the video,
 * after VIDEO_META_TIMEOUT_MS.
 */
export function readVideoMeta(file: File): Promise<{
  width?: number;
  height?: number;
  duration?: number;
  thumbnail?: Blob;
}> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;

    let done = false;
    const finish = (result: {
      width?: number;
      height?: number;
      duration?: number;
      thumbnail?: Blob;
    }) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(result);
    };
    const basics = () => ({
      width: video.videoWidth || undefined,
      height: video.videoHeight || undefined,
      duration: Number.isFinite(video.duration) ? video.duration : undefined,
    });
    const timer = setTimeout(() => finish(basics()), VIDEO_META_TIMEOUT_MS);

    const capture = () => {
      const { width, height } = basics();
      if (!width || !height) {
        finish(basics());
        return;
      }
      try {
        const scale = Math.min(1, 640 / Math.max(width, height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        canvas
          .getContext('2d')
          ?.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => finish({ ...basics(), thumbnail: blob ?? undefined }),
          'image/jpeg',
          0.8
        );
      } catch {
        finish(basics());
      }
    };

    // A frame is decoded once readyState reaches HAVE_CURRENT_DATA.
    const captureWhenReady = () => {
      if (video.readyState >= 2) capture();
      else video.addEventListener('loadeddata', capture, { once: true });
    };

    video.addEventListener(
      'loadedmetadata',
      () => {
        const target = Math.min(1, (video.duration || 0) / 2);
        if (!(target > 0)) {
          captureWhenReady();
          return;
        }
        video.addEventListener('seeked', captureWhenReady, { once: true });
        try {
          video.currentTime = target;
        } catch {
          captureWhenReady();
        }
      },
      { once: true }
    );
    video.addEventListener('error', () => finish({}), { once: true });
    video.src = url;
    video.load();
  });
}

/**
 * Applies a MESSAGE_READ event: `userId` has read `messageIds` and, with
 * read watermarks (D122), everything up to `readUpTo` they didn't send.
 */
export function applyRead(
  list: ChatMessage[],
  event: { userId: string; messageIds: string[]; readUpTo?: string }
): ChatMessage[] {
  const read = new Set(event.messageIds);
  const upTo = event.readUpTo ? Date.parse(event.readUpTo) : null;
  return list.map((message) => {
    const isRead =
      read.has(message.id) ||
      (upTo !== null &&
        message.sender?.id !== event.userId &&
        Date.parse(message.createdAt) <= upTo);
    // A call's summary line arrives over the socket without `readBy`; a
    // read receipt after a call used to crash the open chat.
    const readBy = message.readBy ?? [];
    return isRead && !readBy.includes(event.userId)
      ? { ...message, readBy: [...readBy, event.userId] }
      : message;
  });
}

/**
 * Merges a freshly fetched newest page into what's on screen after a
 * reconnect: the server's copies win (edits, deletes, reactions), messages
 * only on screen stay (older pages, live arrivals), and the result is in
 * time order. Messages missed while the socket was down appear without
 * reopening the chat (2026-10-22).
 */
export function mergeNewest(
  shown: ChatMessage[],
  fresh: ChatMessage[]
): ChatMessage[] {
  const byId = new Map(shown.map((message) => [message.id, message]));
  for (const message of fresh) byId.set(message.id, message);
  return [...byId.values()].sort((a, b) =>
    a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0
  );
}

// Jump to unread (M62, docs/chat-round-four.md D229).

/** Where the "N unread messages" divider goes when a chat opens. */
export interface UnreadMarker {
  /** The first unread message: the divider sits above it. */
  messageId: string;
  /** Unread messages loaded from there down. */
  count: number;
}

/**
 * Unread is the server's rule (D122): someone else sent it, it isn't
 * deleted, and my watermark hasn't reached it. `readBy` includes me once it
 * has, so the page as loaded (before marking read) says which.
 */
function isUnreadBy(message: ChatMessage, userId: string): boolean {
  return (
    Boolean(message.sender) &&
    message.sender!.id !== userId &&
    !message.deletedAt &&
    !message.readBy.includes(userId)
  );
}

/** The divider for a chat as it opened, or null when nothing is unread. */
export function unreadMarker(
  messages: ChatMessage[],
  userId: string
): UnreadMarker | null {
  const first = messages.findIndex((message) => isUnreadBy(message, userId));
  if (first === -1) return null;
  const count = messages
    .slice(first)
    .filter((message) => isUnreadBy(message, userId)).length;
  return { messageId: messages[first]!.id, count };
}

/**
 * Every message from someone else on the page is unread, so the first
 * unread one may be further back: load older pages before placing the
 * divider (up to UNREAD_SEARCH_LIMIT messages).
 */
export function firstUnreadMayBeOlder(
  messages: ChatMessage[],
  userId: string
): boolean {
  const fromOthers = messages.filter(
    (message) =>
      message.sender && message.sender.id !== userId && !message.deletedAt
  );
  return (
    fromOthers.length > 0 &&
    fromOthers.every((message) => isUnreadBy(message, userId))
  );
}

/** How far back the app looks for the first unread message (D229). */
export const UNREAD_SEARCH_LIMIT = 500;
export const UNREAD_PAGE_SIZE = 100;
/** The search gives up after this long and places the divider from what's loaded. */
export const UNREAD_SEARCH_TIMEOUT_MS = 4000;

/**
 * The quote "Reply privately" puts in the direct chat's message box (M63,
 * D235): "↩︎ Priya: <the first 120 characters>" and a new line. Plain text
 * the sender can edit; nothing links it back to the group message.
 */
export function privateQuote(
  message: Pick<ChatMessage, 'type' | 'content' | 'e2ee'>,
  senderName: string,
  limit: number = CHAT_LIMITS.PRIVATE_QUOTE_LENGTH
): string {
  // A message still waiting for its key has no words to quote (D57).
  const label =
    (message.e2ee?.locked ? '' : replyLabel(message)) ||
    t('chat.home.encrypted');
  // By characters, not UTF-16 units, so an emoji or a Hindi letter isn't cut
  // in half.
  const chars = Array.from(label);
  const excerpt =
    chars.length > limit
      ? `${chars.slice(0, limit).join('').trimEnd()}…`
      : label;
  return `↩︎ ${senderName}: ${excerpt}\n`;
}

/**
 * "{Name}'s devices changed" (M73, D303), or "{Name} was added to this chat"
 * / "{Name} joined the family" (D343): a line on this device only, at the
 * moment it was noticed. Not a message — never sent, unread or searched.
 */
export interface DeviceNotice {
  /** `devices` (D303, opens their code) unless said; `added`, `joined` (D343). */
  kind?: 'devices' | 'added' | 'joined';
  userId: string;
  /** How I see them (D143): my label for them, or their first name. */
  name: string;
  /** When this device noticed (ms). */
  at: number;
}

/**
 * Where the device notices go among the loaded messages (D303): each above
 * the first message after it, or after the last. One older than the loaded
 * window waits until that page loads; one newer than a window that stops
 * short of the newest message (D271) waits likewise.
 */
export function placeDeviceNotices(
  messages: readonly Pick<ChatMessage, 'id' | 'createdAt'>[],
  notices: readonly DeviceNotice[],
  window: { hasMore: boolean; hasNewer: boolean }
): { before: Map<string, DeviceNotice[]>; after: DeviceNotice[] } {
  const before = new Map<string, DeviceNotice[]>();
  const after: DeviceNotice[] = [];
  const times = messages.map((m) => new Date(m.createdAt).getTime());
  for (const notice of [...notices].sort((a, b) => a.at - b.at)) {
    const index = times.findIndex((time) => time > notice.at);
    if (index === -1) {
      if (!window.hasNewer) after.push(notice);
    } else if (index > 0 || !window.hasMore) {
      const id = messages[index]!.id;
      before.set(id, [...(before.get(id) ?? []), notice]);
    }
  }
  return { before, after };
}

/**
 * The D343 lines for one chat: each current participant other than me who
 * joined it after this device's first look, or — in the family's default
 * group — joined the family, which wins for that person. Newest per person;
 * none older than the chat itself.
 */
export function memberJoinNotices(
  chat: {
    id: string;
    familyId: string;
    isDefault?: boolean;
    createdAt: string;
    participants: ReadonlyArray<{ id: string; displayName: string }>;
  },
  joins: Readonly<Record<string, Readonly<Record<string, number>>>>,
  me: string | undefined,
  nameOf: (participant: { id: string; displayName: string }) => string
): DeviceNotice[] {
  const since = Date.parse(chat.createdAt);
  const inChat = joins[`chat:${chat.id}`] ?? {};
  const inFamily = chat.isDefault
    ? (joins[`family:${chat.familyId}`] ?? {})
    : {};
  return chat.participants.flatMap((participant): DeviceNotice[] => {
    if (participant.id === me) return [];
    const family = inFamily[participant.id];
    const kind = family !== undefined ? 'joined' : 'added';
    const at = family ?? inChat[participant.id];
    if (at === undefined) return [];
    if (Number.isFinite(since) && at < since) return [];
    return [{ kind, userId: participant.id, name: nameOf(participant), at }];
  });
}

/**
 * Your direct chat with someone, if there is one: where a face, "Wish them"
 * and a Rakhi or Bhai Dooj nudge go (D144, D315). Without one, the caller
 * starts it. A group they're in never counts.
 */
export function directChatWith<
  C extends { type: string; participants: ReadonlyArray<{ id: string }> },
>(conversations: readonly C[], userId: string): C | null {
  return (
    conversations.find(
      (c) => c.type === 'direct' && c.participants.some((p) => p.id === userId)
    ) ?? null
  );
}
